/* eslint-disable import/first */
// Process polyfill for simple-peer
import process from 'process/browser';
if (typeof window !== 'undefined' && typeof window.process === 'undefined') {
  window.process = process;
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';

import { ThemeProvider } from './contexts/ThemeContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { registerServiceWorker, isDevelopment, setupServiceWorkerMessageListener } from './utils/serviceWorkerUtils';

// Intercept XMLHttpRequest to suppress Razorpay unsafe header errors
(function() {
  const OriginalXHR = window.XMLHttpRequest;
  window.XMLHttpRequest = function() {
    const xhr = new OriginalXHR();
    const originalGetResponseHeader = xhr.getResponseHeader;
    
    xhr.getResponseHeader = function(headerName) {
      // Suppress errors when trying to get unsafe headers from Razorpay
      if (headerName && headerName.toLowerCase() === 'x-rtb-fingerprint-id') {
        try {
          return originalGetResponseHeader.call(this, headerName);
        } catch (e) {
          // Silently ignore - this is a browser security restriction
          return null;
        }
      }
      return originalGetResponseHeader.call(this, headerName);
    };
    
    return xhr;
  };
})();

// Initialize Sentry
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    // Filter out known safe errors
    beforeSend(event, hint) {
      const error = hint.originalException || hint.syntheticException;
      const errorMessage = error?.message || '';
      
      // Filter out known safe errors
      if (
        errorMessage.includes('x-rtb-fingerprint-id') ||
        errorMessage.includes('Expected length, "auto"') ||
        errorMessage.includes('Tracking Prevention') ||
        errorMessage.includes('preloaded using link preload') ||
        errorMessage.includes('not used within a few seconds') ||
        errorMessage.includes('@firebase') ||
        errorMessage.includes('Firestore') ||
        errorMessage.includes('enableIndexedDbPersistence')
      ) {
        return null; // Don't send this event
      }
      
      return event;
    },
  });
}

// Suppress ALL console output to catch warnings
// NOTE: DISABLED FOR DEBUGGING - Re-enable in production
const suppressAllConsole = () => {
  // DISABLED - Allow all console logs for debugging
  return;
  
  /* DISABLED CODE BELOW
  // Allow console.warn for WebRTC/live class debugging
  const originalWarn = console.warn;
  console.warn = function(...args) {
    // Check message for preload warnings
    const message = args.map(arg => {
      if (typeof arg === 'string') return arg;
      if (arg && typeof arg === 'object') {
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');
    
    // Suppress preload warnings specifically
    if (
      message.includes('preloaded using link preload') ||
      message.includes('not used within a few seconds') ||
      message.includes('was preloaded') ||
      message.includes('Please make sure it has an appropriate') ||
      message.includes('static/js/main.js') ||
      message.includes('static/css/main.css') ||
      message.includes('fonts.googleapis.com')
    ) {
      return;
    }

    // Suppress manifest serviceworker warnings
    if (
      message.includes('serviceworker') ||
      message.includes('web app manifest') ||
      message.includes('must be a dictionary')
    ) {
      return;
    }

    // Suppress Razorpay unsafe header warnings
    if (
      message.includes('Refused to get unsafe header') ||
      message.includes('x-rtb-fingerprint-id') ||
      message.includes('unsafe header')
    ) {
      return;
    }

    // Suppress manifest serviceworker warnings
    if (
      message.includes('serviceworker') ||
      message.includes('web app manifest') ||
      message.includes('must be a dictionary')
    ) {
      return;
    }
    
    // Allow WebRTC/live class warnings
    if (
      message.includes('🔗') || message.includes('🔌') || message.includes('✅') || 
      message.includes('❌') || message.includes('⚠️') || message.includes('📤') ||
      message.includes('⏳') || message.includes('Peer') || message.includes('peer') ||
      message.includes('stream') || message.includes('track') || message.includes('WebRTC') ||
      message.includes('signaling') || message.includes('connection') || message.includes('liveclass') ||
      message.includes('LiveClass') || message.includes('participant')
    ) {
      originalWarn.apply(console, args);
      return;
    }
    
    // Suppress all other warnings
    return;
  };

  // Suppress console.info completely
  const originalInfo = console.info;
  console.info = function() {
    // Suppress all info logs
    return;
  };

  // Allow console.log for WebRTC/live class debugging
  const originalLog = console.log;
  console.log = function(...args) {
    const message = args.map(arg => {
      if (typeof arg === 'string') return arg;
      if (arg && typeof arg === 'object') {
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');
    
    // Allow WebRTC/live class debugging messages
    if (
      message.includes('🔗') || message.includes('🔌') || message.includes('✅') || 
      message.includes('❌') || message.includes('⚠️') || message.includes('📤') ||
      message.includes('⏳') || message.includes('Peer') || message.includes('peer') ||
      message.includes('stream') || message.includes('track') || message.includes('WebRTC') ||
      message.includes('signaling') || message.includes('connection') || message.includes('Creating peer') ||
      message.includes('Received stream') || message.includes('Added') || message.includes('Replaced') ||
      message.includes('Connected to') || message.includes('Sending') || message.includes('liveclass') ||
      message.includes('LiveClass') || message.includes('participant')
    ) {
      originalLog.apply(console, args);
      return;
    }
    
    // Suppress other logs for cleaner console
    return;
  };

    // Suppress console.error for known safe errors
    const originalError = console.error;
    console.error = function(...args) {
      const message = args.map(arg => {
        if (typeof arg === 'string') return arg;
        if (arg && typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');

      // Suppress Razorpay SVG attribute errors
      if (
        message.includes('<svg> attribute width') ||
        message.includes('<svg> attribute height') ||
        message.includes('Unexpected end of attribute') ||
        message.includes('Expected length, ""')
      ) {
        return;
      }

      // Suppress Razorpay checkout frame errors
      if (
        message.includes('checkout-frame.modern.js') ||
        message.includes('checkout-static-next') ||
        message.includes('v2-entry.modern.js') ||
        message.includes('v2-entry-app') ||
        message.includes('v2-entry-') ||
        message.includes('v2-entry')
      ) {
        return;
      }

      // Suppress Razorpay tracking endpoint errors
      if (
        message.includes('lumberjack.razorpay.com') ||
        message.includes('razorpay.com/v1/track') ||
        (message.includes('Failed to load resource') && message.includes('razorpay'))
      ) {
        return;
      }

      // Suppress Razorpay unsafe header errors (catch all variations)
      if (
        message.includes('Refused to get unsafe header') ||
        message.includes('x-rtb-fingerprint-id') ||
        message.includes('unsafe header') ||
        message.includes('getResponseHeader') ||
        (message.includes('Refused') && message.includes('header'))
      ) {
        return;
      }

      // Suppress manifest serviceworker warnings
      if (
        message.includes('serviceworker') ||
        message.includes('web app manifest') ||
        message.includes('must be a dictionary')
      ) {
        return;
      }

      // Suppress Firebase errors
      if (
        message.includes('@firebase') ||
        message.includes('Firestore') ||
        message.includes('enableIndexedDbPersistence') ||
        message.includes('will be deprecated') ||
        message.includes('FirestoreSettings.cache')
      ) {
        return;
      }

      // Suppress browser errors
      if (
        message.includes('Tracking Prevention') ||
        message.includes('preloaded using link preload') ||
        message.includes('not used within a few seconds') ||
        message.includes('was preloaded') ||
        message.includes('Please make sure it has an appropriate') ||
        message.includes('static/js/main.js') ||
        message.includes('static/css/main.css') ||
        message.includes('fonts.googleapis.com')
      ) {
        return;
      }

      // Suppress React errors
      if (
        message.includes('Warning:') ||
        message.includes('React Hook') ||
        message.includes('useEffect') ||
        message.includes('useCallback')
      ) {
        return;
      }

      // Allow WebRTC/live class errors
      if (
        message.includes('🔗') || message.includes('🔌') || message.includes('✅') || 
        message.includes('❌') || message.includes('⚠️') || message.includes('📤') ||
        message.includes('⏳') || message.includes('Peer') || message.includes('peer') ||
        message.includes('stream') || message.includes('track') || message.includes('WebRTC') ||
        message.includes('signaling') || message.includes('connection') || message.includes('liveclass') ||
        message.includes('LiveClass') || message.includes('participant') || message.includes('RTCPeerConnection') ||
        message.includes('setLocalDescription') || message.includes('setRemoteDescription')
      ) {
        originalError.apply(console, args);
        return;
      }
      
      // Suppress other errors
      return;
    };
  */
};

// DISABLED - Don't suppress console for debugging
// suppressAllConsole();

// Global error handler to suppress known third-party errors
window.addEventListener('error', (event) => {
  // Suppress RTB fingerprint header errors from Razorpay
  if (event.message && (
    event.message.includes('x-rtb-fingerprint-id') ||
    event.message.includes('Refused to get unsafe header')
  )) {
    event.preventDefault();
    return false;
  }
  
  // Suppress SVG attribute errors from Razorpay's checkout frame
  if (event.message && (
    event.message.includes('<svg> attribute width') ||
    event.message.includes('<svg> attribute height') ||
    event.message.includes('Unexpected end of attribute') ||
    event.message.includes('Expected length, ""')
  )) {
    event.preventDefault();
    return false;
  }
  
  // Suppress errors from Razorpay's checkout frame files
  if (event.filename && (
    event.filename.includes('checkout-frame.modern.js') ||
    event.filename.includes('checkout-static-next') ||
    event.filename.includes('v2-entry.modern.js') ||
    event.filename.includes('v2-entry-app') ||
    event.filename.includes('v2-entry-')
  )) {
    event.preventDefault();
    return false;
  }

  // Suppress manifest serviceworker warnings
  if (event.message && (
    event.message.includes('serviceworker') ||
    event.message.includes('web app manifest')
  )) {
    event.preventDefault();
    return false;
  }

  // Suppress tracking prevention errors
  if (event.message && event.message.includes('Tracking Prevention')) {
    event.preventDefault();
    return false;
  }

  // Suppress preload warnings
  if (event.message && (
    event.message.includes('preloaded using link preload') ||
    event.message.includes('not used within a few seconds')
  )) {
    event.preventDefault();
    return false;
  }
});

// Suppress unhandled promise rejections from Razorpay
window.addEventListener('unhandledrejection', (event) => {
  const errorMessage = event.reason?.message || String(event.reason || '');
  
  // Suppress Razorpay SVG errors
  if (
    errorMessage.includes('<svg> attribute width') ||
    errorMessage.includes('<svg> attribute height') ||
    errorMessage.includes('Unexpected end of attribute')
  ) {
    event.preventDefault();
    return false;
  }

  // Suppress Razorpay unsafe header errors
  if (
    errorMessage.includes('Refused to get unsafe header') ||
    errorMessage.includes('x-rtb-fingerprint-id')
  ) {
    event.preventDefault();
    return false;
  }
});

// Suppress network errors from Razorpay's tracking endpoint (400 errors are harmless)
const originalFetch = window.fetch;
window.fetch = function(...args) {
  const url = args[0]?.toString() || '';
  
  // Suppress Razorpay tracking endpoint errors (these are analytics and not critical)
  if (url.includes('lumberjack.razorpay.com') || url.includes('razorpay.com/v1/track')) {
    return originalFetch.apply(this, args).catch(() => {
      // Silently ignore tracking errors - they're not critical for payment processing
      return Promise.resolve(new Response(null, { status: 200, statusText: 'OK' }));
    });
  }
  
  return originalFetch.apply(this, args);
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <RealtimeProvider>
        <App />
      </RealtimeProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// Register service worker for caching and offline support
window.addEventListener('load', async () => {
  try {
    const registration = await registerServiceWorker('/sw.js');
    
    if (registration) {
      // Service Worker registered successfully
      
      // Setup message listener
      setupServiceWorkerMessageListener((data) => {
        if (data && data.type === 'SW_UPDATE_AVAILABLE') {
          // Service worker update available
          if (isDevelopment()) {
            // In development, automatically reload
            window.location.reload();
          }
        }
      });
      
      // Handle service worker controller changes
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Service worker controller changed
        if (isDevelopment()) {
          // In development, reload the page when controller changes
          window.location.reload();
        }
      });
    } else {
      // Service Worker registration failed or not supported
    }
  } catch (error) {
    // Error during Service Worker setup
  }
});
